import { useMutation } from "@apollo/client";
import { Card, H5, H6, Button, Collapse, Alert, Intent } from "@blueprintjs/core";
import React, { useCallback, useContext, useState } from "react";
import ReactJson from "react-json-view";
import { DELETE_ACTION } from "graphql/mutations";
import { GET_POLICY_ACTIONS } from "graphql/queries";
import { PolicyAction } from "types";
import { SelectedProjectContext } from "utils/context";
import ActionsDialog from "./ActionsDialog";
import { ActionCardToaster } from "utils/toasters";

type Props = { action: PolicyAction };

export default function ActionCard({ action }: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [showButtons, setshowButtons] = useState(false);
    const [deleteAction] = useMutation(DELETE_ACTION);
    const [project] = useContext(SelectedProjectContext);
    const [loading, setLoading] = useState(false);

    const toggleDelete = useCallback(() => {
        setDeleteOpen(!deleteOpen);
    }, [deleteOpen]);

    const toggleDialog = useCallback(() => {
        setDialogOpen(!dialogOpen);
    }, [dialogOpen]);

    const handleDeleteConfirm = useCallback(() => {
        deleteAction({
            variables: { actionId: action.id },
            refetchQueries: [
                {
                    query: GET_POLICY_ACTIONS,
                    variables: { projectId: project?.projectId }
                }
            ]
        })
            .finally(() => {
                ActionCardToaster.show({
                    message: `Successfully Deleted Action`,
                    intent: "warning"
                });
                setLoading(false);

                setTimeout(toggleDelete, 1000);
            })
            .catch(() => {
                ActionCardToaster.show({
                    message: `Failed to Delete Action`,
                    intent: "danger"
                });
                setLoading(false);
                setTimeout(toggleDelete, 1000);
            });
    }, [action, deleteAction, project, toggleDelete]);

    const handleEdit = useCallback(() => {
        toggleDialog();
    }, [toggleDialog]);

    //todo: maybe move the ActionsDialog one level up?
    return (
        <>
            <ActionsDialog isOpen={dialogOpen} toggle={toggleDialog} action={action} buttonText="Edit" />
            <Alert
                cancelButtonText="Cancel"
                confirmButtonText="Confirm"
                icon="trash"
                intent={Intent.DANGER}
                isOpen={deleteOpen}
                loading={loading}
                onCancel={toggleDelete}
                onConfirm={handleDeleteConfirm}
            >
                <p>
                    Are you sure you want to delete <b>{action.name} </b>? This cannot be undone!
                </p>
            </Alert>

            <Card
                className="card"
                elevation={2}
                onMouseEnter={() => setshowButtons(true)}
                onMouseLeave={() => setshowButtons(false)}
            >
                <H5>Action: {action.name}</H5>
                <H6>Action Type: {action.type}</H6>
                <div className="collapse">
                    <Button
                        minimal
                        icon={isOpen ? "caret-up" : "caret-down"}
                        text="View Details"
                        onClick={() => setIsOpen(!isOpen)}
                    />
                    <Collapse isOpen={isOpen}>
                        <ReactJson
                            name="policy"
                            src={action}
                            style={{
                                textAlign: "left",
                                fontSize: "1rem",
                                wordBreak: "break-all"
                            }}
                            displayDataTypes={false}
                            displayObjectSize={false}
                            enableClipboard={false}
                            theme="ashes"
                            iconStyle="square"
                        />
                    </Collapse>
                </div>
                {showButtons && (
                    <div className="card-actions">
                        <Button text="Edit" intent="primary" icon="edit" onClick={handleEdit} />
                        <Button text="Delete" intent="danger" icon="trash" onClick={toggleDelete} />
                    </div>
                )}
            </Card>
        </>
    );
}
